import React from 'react';
import { Link } from 'react-router-dom';

import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import {
	anticipatoryRichText,
	blockRichImgCaptures,
	blockRichText,
	blockRichTitle,
	copingBlockText,
	copingBlockTitle,
	counselingRichText,
	distTherapyRichText,
	navItems,
	petLossSupMastheadText,
	petLossSupportLinkText,
	supportRichText,
} from '@app/components/Pages/PetLossSupport/PetLossSupportContent';
import CardLinks from '@app/components/UI/CardLinks/CardLinks';
import FiftyFifty from '@app/components/UI/FiftyFifty/FiftyFifty';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import BlockRich from '@app/components/UI/LinkBlock/BlockRich';
import Masthead from '@app/components/UI/MastHead/Masthead';
import NavTabs from '@app/components/UI/NavTabs/NavTabs';
import RichText from '@app/components/UI/RichText/RichText';
import beginningToHeal from '@app/content/Beginning-to-heal.jpg';
import childrenPetLoss from '@app/content/Children-pet-loss.jpg';
import resourceLine from '@app/content/Resource-line.jpg';
import { useIsMobile } from '@app/hooks/useIsMobile';
import { LinkItem } from '@app/objects/LinkItem';

interface State {
	supportGroups: Array<LinkItem>;
	unCounseling: Array<LinkItem>;
	unResources: Array<LinkItem>;
	bookResources: Array<LinkItem>;
}

const ImageCapture = ({ name, town, title }) => {
	return (
		<>
			<span>
				{`${name}, ${town}`}
			</span>
			<br />
			<span className="h4">{title}</span>
		</>
	);
};

const PetLossSupport = () => {
	const params = [
		{ name: 'supportGroups', title: 'Pet loss support groups' },
		{ name: 'unCounseling', title: 'Universities that offer counseling' },
	];

	const [state, setState] = React.useState<State>({
		supportGroups: [],
		unCounseling: [],
		unResources: [],
		bookResources: [],
	});

	const navCollapse = useIsMobile();
	const [currentIndex, setCurrentIndex] = React.useState<number>(0);

	const anticipatoryRef = React.useRef<any>();
	const supportRef = React.useRef<any>();
	const counselingRef = React.useRef<any>();
	const copingRef = React.useRef<any>();

	const onSetIssue = (index) => {
		const refs = [anticipatoryRef, supportRef, counselingRef, copingRef];
		let num: number = index;

		if (navCollapse) {
			navItems.forEach((item, i: number) => {
				if (item.title === index) {
					num = i;
				}
			});
		}

		setCurrentIndex(num);
		refs[num]?.current?.scrollIntoView({ block: 'start' });
	};

	const fetchAllFunc = () => {
		Promise.all(
			params.map((item) => {
				const tag = item.title.charAt(0).toLowerCase() + item.title.slice(1);
				return rest.get<List<LinkItem>>(`v1/links/${tag}`).then((res) => ({
					name: item.name,
					value: res.list,
				}));
			}),
		)
			.then((res) => {
				const state = {};
				res.forEach((item) => {
					item.value[1].url = 'https://www.everlifememorials.com/Pet-Loss-Support-Groups-s/443.htm';
					state[item.name] = item.value;
				});
				setState(state as State);
			})
			.catch((error) => console.log(error, 'ERROR'));
	};

	React.useEffect(() => {
		fetchAllFunc();
	}, []);

	React.useEffect(() => {
		if (window.scrollY > 800) {
			window.addEventListener('scroll', () => {
				window.scrollY <= 500 ? setCurrentIndex(0) : null;
			});
		}
	}, []);

	return (
		<HelmetProvider>
			<div className="pet-loss-sup">
				<Masthead
					title="Pet Loss and Grief Resources"
					imageSrc={require('@app/content/petLossSupHeader.jpg')}
					text={petLossSupMastheadText}
					buttonText={
						<Link to="/our-services/pet-loss-support">
							<button type="button" className="btn btn-md btn-primary">
								Pet Loss Support
							</button>
						</Link>
					}
					btnPath="/"
					onBtnClick={() => {}}
					imageAlt="Loss of a pet"
				/>
				<NavTabs navItems={navItems} currentIndex={currentIndex} onSetIssue={onSetIssue} navCollapse={navCollapse} onChange={onSetIssue} />
				<FiftyFifty
					title={copingBlockTitle}
					text={copingBlockText}
					src={require('@app/content/petLossCoping.png')}
					reversed
					alt="Pet loss grief"
				/>
				<RichText title="Anticipatory grief" text={anticipatoryRichText} name={navItems[0].name} refRichText={anticipatoryRef} />
				<RichText title="Support groups" text={supportRichText} name={navItems[1].name} refRichText={supportRef} />
				<RichText text={petLossSupportLinkText} />
				<RichText title="Pet loss support groups" text="" />
				<CardLinks list={state.supportGroups} />
				<RichText title="Distance therapy" text={distTherapyRichText} />
				<RichText title="Universities that offer counseling" text={counselingRichText} name={navItems[2].name} refRichText={counselingRef} />
				<CardLinks list={state.unCounseling} />
				<BlockRich
					title={blockRichTitle}
					text={blockRichText}
					blockClassName="link-block-rich_modern"
					images={[
						{
							title: (
								<ImageCapture
									name={blockRichImgCaptures[2].name}
									town={blockRichImgCaptures[2].town}
									title={blockRichImgCaptures[2].title}
								/>
							),
							src: beginningToHeal,
							link: blockRichImgCaptures[2].link,
							alt: 'Healing pet loss',
						},
						{
							title: (
								<ImageCapture
									name={blockRichImgCaptures[1].name}
									town={blockRichImgCaptures[1].town}
									title={blockRichImgCaptures[1].title}
								/>
							),
							src: childrenPetLoss,
							link: blockRichImgCaptures[1].link,
							alt: 'Children pet loss',
						},
						{
							title: (
								<ImageCapture
									name={blockRichImgCaptures[0].name}
									town={blockRichImgCaptures[0].town}
									title={blockRichImgCaptures[0].title}
								/>
							),
							src: resourceLine,
							link: blockRichImgCaptures[0].link,
							alt: 'Pet Loss Services',
						},
					]}
					subtitle="More on pet loss"
					name={navItems[3].name}
					refBlockRich={copingRef}
				/>
			</div>
		</HelmetProvider>
	);
};

export default PetLossSupport;
